'use client';

import {
  Box,
  type BoxProps,
  Button,
  ClientOnly,
  HStack,
  IconButton,
  Image,
  Separator,
  Stack,
  StackSeparator,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { MdExpandMore, MdLanguage, MdOutlineExpandMore } from 'react-icons/md';
import { useImmer } from 'use-immer';

import { i18n } from '@/__generated__/i18n';
import { useI18n, useLocaleValue } from '@/__generated__/i18n.next';
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
} from '@/components/__generated__/chakra-ui/drawer';
import { LinkButton } from '@/components/__generated__/chakra-ui/link-button';
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  MenuTriggerItem,
} from '@/components/__generated__/chakra-ui/menu';
import { PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from '@/components/__generated__/chakra-ui/popover';
import { NextLinkWithoutPrefetch } from '@/components/atoms/NextLinkWithoutPrefetch';
import type { ReadabilityNotificationItem } from '@/features/lockReviewRead/notifications';
import { authClient } from '@/infrastructures/betterAuth/authClient';
import type { Organization } from '@/infrastructures/db/schema';
import { backendOrpc } from '@/infrastructures/orpcBackend/client';
import { dayjs } from '@/utils/dayjs';
import { getPathnameWithoutLocale } from '@/utils/url';

import { ContestRemainingTime } from '../../app/[locale]/(authorized)/(contest)/contests/[contestId]/ContestRemainingTime';
import { LOCALES_INCLUDING_NON_STANDARD } from '../../constants';
import { Icon } from '../atoms/Icon';

type Props = {
  closedAt?: Date;
  contestId?: string;
  isVisible?: boolean;
  joinedOrganizations: Pick<Organization, 'id' | 'name'>[];
  organizationInvitations: { organization: Pick<Organization, 'id' | 'name'> }[];
  readabilityNotifications?: ReadabilityNotificationItem[];
  user: { email: string | undefined; isAdmin: boolean; userId: number } | undefined;
} & Omit<BoxProps, 'direction'>;

export const DefaultHeaderOnClient: React.FC<Props> = ({
  closedAt,
  contestId,
  isVisible,
  joinedOrganizations,
  organizationInvitations,
  readabilityNotifications: readabilityNotificationsOnServer = [],
  user,
  ...boxProps
}) => {
  const t = useI18n();
  const router = useRouter();
  const markNotificationReadMutation = useMutation(backendOrpc.lockReviewRead.markNotificationRead.mutationOptions());
  // Client navigation keeps this layout's header, so hide opened notifications until the next server render.
  const [openedNotificationIds, updateOpenedNotificationIds] = useImmer<number[]>([]);
  const readabilityNotifications = readabilityNotificationsOnServer.filter(
    (notification) => !openedNotificationIds.includes(notification.id)
  );
  const locale = useLocaleValue();

  const pathname = usePathname();
  const pathnameWithoutLocale = getPathnameWithoutLocale(pathname);
  const searchParams = useSearchParams();

  return (
    <HStack data-testid="default-header" borderBottomWidth={1} gap={4} h={12} px={2} {...boxProps}>
      <Box display={{ base: 'block', md: 'none' }}>
        {user && <MobileMenu pathnameWithoutLocale={pathnameWithoutLocale} />}
      </Box>
      <HStack flexGrow={1} flexShrink={1} gap={2}>
        <LinkButton href={`/${locale}`} px={1} variant="ghost">
          <Image alt={t.core.title()} h={6} src="/language-neutral/images/exercode.svg" />
        </LinkButton>
        <HStack display={{ base: 'none', md: 'flex' }} flexGrow={0} flexShrink={0} gap={0}>
          {getMenuItems(locale, contestId, isVisible).map(([href, label]) => (
            <Button key={href} asChild size="sm" variant="ghost">
              <NextLinkWithoutPrefetch href={href}>{label}</NextLinkWithoutPrefetch>
            </Button>
          ))}
          <MenuRoot unmountOnExit id="locale">
            <MenuTrigger asChild>
              <Button size="sm" variant="ghost">
                <HStack>
                  <Box display={{ base: 'block', lg: 'none' }}>
                    <MdLanguage />
                  </Box>
                  <Box display={{ base: 'none', lg: 'block' }}>{t.components.header.locales[locale]()}</Box>
                  <MdExpandMore />
                </HStack>
              </Button>
            </MenuTrigger>
            <MenuContent>
              {LOCALES_INCLUDING_NON_STANDARD.map((l) => (
                <MenuItem key={l} asChild value={l}>
                  <NextLinkWithoutPrefetch
                    href={{ pathname: `/${l}${pathnameWithoutLocale}`, query: searchParams.toString() }}
                    prefetch={false}
                  >
                    {t.components.header.locales[l]()}
                  </NextLinkWithoutPrefetch>
                </MenuItem>
              ))}
            </MenuContent>
          </MenuRoot>
          <MenuRoot unmountOnExit id="related-sites">
            <MenuTrigger asChild>
              <Button size="sm" variant="ghost">
                <HStack>
                  <span>{t.components.header.relatedSites()}</span>
                  <MdExpandMore />
                </HStack>
              </Button>
            </MenuTrigger>
            <MenuContent>
              {getRelatedSiteItems(locale).map(([href, label]) => (
                <MenuItem key={href} asChild value={href}>
                  <NextLinkWithoutPrefetch href={href} rel="noopener noreferrer" target="_blank">
                    <HStack>
                      <Text>{label}</Text>
                      <Icon icon="openInNew" />
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
              ))}
            </MenuContent>
          </MenuRoot>
          {user?.isAdmin && (
            <NextLinkWithoutPrefetch href={`/${locale}/admin`}>
              <Button colorPalette="red" size="sm" variant="ghost">
                <HStack>
                  <Icon icon="private" />
                  <Box as="span" display={{ base: 'none', lg: 'inline' }}>
                    {t.components.header.administration()}
                  </Box>
                </HStack>
              </Button>
            </NextLinkWithoutPrefetch>
          )}
        </HStack>
      </HStack>

      <Box flexGrow={0} flexShrink={0}>
        {user ? (
          <HStack>
            {closedAt && (
              <ClientOnly>
                <PopoverRoot lazyMount unmountOnExit id="contest-timer">
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="ghost">
                      <HStack>
                        <Icon icon="clock" />
                        <ContestRemainingTime closedAt={closedAt} locale={locale} />
                      </HStack>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent py={2}>
                    <PopoverBody>
                      <Text>
                        {t.components.contestTimer.endTime()}: {dayjs(closedAt).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </PopoverRoot>
              </ClientOnly>
            )}
            <PopoverRoot lazyMount unmountOnExit id="notification">
              <PopoverTrigger asChild>
                <IconButton aria-label="notifications" size="sm" variant="ghost">
                  <Box pos="relative">
                    <Icon icon="notifications" />
                    {organizationInvitations.length + readabilityNotifications.length > 0 && (
                      <Icon
                        boxSize="0.5em"
                        color="fg.error"
                        icon="circle"
                        position="absolute"
                        right={0}
                        top={0}
                        transform="translate(25%, -25%)"
                      />
                    )}
                  </Box>
                </IconButton>
              </PopoverTrigger>
              <PopoverContent py={2}>
                <Stack gap={0} separator={<StackSeparator />}>
                  {organizationInvitations.length + readabilityNotifications.length === 0 && (
                    <PopoverBody color="fg.muted" userSelect="none">
                      {t.core.none()}
                    </PopoverBody>
                  )}
                  {readabilityNotifications.map((notification) => (
                    <PopoverBody
                      key={`readability-${notification.id}`}
                      asChild
                      _hover={{ bg: 'bg.muted' }}
                      cursor="pointer"
                      transitionDuration="normal"
                      transitionProperty="colors"
                    >
                      <NextLinkWithoutPrefetch
                        href={`/${locale}${notification.path}`}
                        onClick={(event) => {
                          event.preventDefault();
                          updateOpenedNotificationIds((draft) => {
                            draft.push(notification.id);
                          });
                          // Mark it read before navigating so that the next server render omits it.
                          void markNotificationReadMutation
                            .mutateAsync({ notificationId: notification.id })
                            .catch((error: unknown) => {
                              console.error(error);
                            })
                            .finally(() => {
                              router.push(`/${locale}${notification.path}`);
                              router.refresh();
                            });
                        }}
                      >
                        {notification.isTop
                          ? t.components.header.readabilityResultsReadyAsTop({
                              problemName: notification.problemName,
                            })
                          : notification.hasTopCommentary
                            ? t.components.header.readabilityResultsReadyWithRanking({
                                problemName: notification.problemName,
                              })
                            : t.components.header.readabilityResultsReadyWithoutCommentary({
                                problemName: notification.problemName,
                              })}
                      </NextLinkWithoutPrefetch>
                    </PopoverBody>
                  ))}
                  {organizationInvitations.map((invitation) => (
                    <PopoverBody
                      key={invitation.organization.id}
                      asChild
                      _hover={{ bg: 'bg.muted' }}
                      cursor="pointer"
                      transitionDuration="normal"
                      transitionProperty="colors"
                    >
                      <NextLinkWithoutPrefetch href={`/${locale}/organizations/${invitation.organization.id}/join`}>
                        <span>
                          {t.components.header.organizationInvitationPrefix()}
                          <strong>{invitation.organization.name}</strong>
                          {t.components.header.organizationInvitationSuffix()}
                        </span>
                      </NextLinkWithoutPrefetch>
                    </PopoverBody>
                  ))}
                </Stack>
              </PopoverContent>
            </PopoverRoot>

            <MenuRoot unmountOnExit id="affiliation">
              <MenuTrigger asChild display={{ base: 'none', lg: 'inline-flex' }}>
                <Button size="sm" variant="ghost">
                  <HStack>
                    <span>{t.models.user.affiliation()}</span>
                    <MdOutlineExpandMore />
                  </HStack>
                </Button>
              </MenuTrigger>
              <MenuContent>
                {joinedOrganizations.length === 0 && (
                  <MenuItem disabled value="none">
                    {t.core.none()}
                  </MenuItem>
                )}
                {joinedOrganizations.map((organization) => (
                  <MenuItem key={organization.id} asChild value={String(organization.id)}>
                    <NextLinkWithoutPrefetch href={`/${locale}/organizations/${organization.id}`}>
                      {organization.name}
                    </NextLinkWithoutPrefetch>
                  </MenuItem>
                ))}
              </MenuContent>
            </MenuRoot>

            <MenuRoot unmountOnExit id="user">
              <MenuTrigger asChild>
                <Button size="sm" variant="ghost">
                  <HStack>
                    <Icon icon="user" />
                    <HStack display={{ base: 'none', lg: 'flex' }}>
                      {!closedAt && <span>{user.email}</span>}
                      <MdOutlineExpandMore />
                    </HStack>
                  </HStack>
                </Button>
              </MenuTrigger>

              <MenuContent>
                <MenuRoot positioning={{ placement: 'left-start', gutter: 2 }}>
                  <MenuTriggerItem display={{ base: 'inline-flex', lg: 'none' }} value="organization">
                    <HStack w="full">
                      <Icon icon="settings" />
                      <Text>{t.models.user.affiliation()}</Text>
                    </HStack>
                  </MenuTriggerItem>
                  <MenuContent>
                    {joinedOrganizations.length === 0 && (
                      <MenuItem disabled value="none">
                        {t.core.none()}
                      </MenuItem>
                    )}
                    {joinedOrganizations.map((organization) => (
                      <MenuItem key={organization.id} asChild value={String(organization.id)}>
                        <NextLinkWithoutPrefetch href={`/${locale}/organizations/${organization.id}`}>
                          {organization.name}
                        </NextLinkWithoutPrefetch>
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuRoot>
                <MenuItem asChild value="submissions">
                  <NextLinkWithoutPrefetch
                    href={{
                      pathname: `/${locale}/submissions`,
                      query: {
                        authorId: 'self',
                      },
                    }}
                  >
                    <HStack>
                      <Icon icon="submissions" />
                      <Text>{t.components.header.submissions()}</Text>
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
                <MenuItem asChild value="settings">
                  <NextLinkWithoutPrefetch href={`/${locale}/settings`}>
                    <HStack>
                      <Icon icon="settings" />
                      <Text>{t.components.header.settings()}</Text>
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
                <MenuItem asChild value="repositories">
                  <NextLinkWithoutPrefetch href={`/${locale}/repositories`}>
                    <HStack>
                      <Icon icon="repository" />
                      <Text>{t.components.header.repositories()}</Text>
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
                <MenuItem asChild value="portfolio">
                  <NextLinkWithoutPrefetch href={`/${locale}/portfolio/mine`}>
                    <HStack>
                      <Icon icon="portfolio" />
                      <Text>{t.components.header.portfolio()}</Text>
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
                <MenuItem asChild value="contact">
                  <NextLinkWithoutPrefetch href={`/${locale}/help/contact`}>
                    <HStack>
                      <Icon icon="contact" />
                      <Text>{t.components.header.contact()}</Text>
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </MenuItem>
                <MenuSeparator />
                <MenuItem value="signOut" onClick={() => void signOut(`/${locale}`)}>
                  <HStack>
                    <Icon icon="signOut" />
                    <Text>{t.components.header.signOut()}</Text>
                  </HStack>
                </MenuItem>
              </MenuContent>
            </MenuRoot>
          </HStack>
        ) : (
          <HStack>
            <LinkButton colorPalette="brand" href="/auth/signin" size="sm">
              {t.components.header.signIn()}
            </LinkButton>
          </HStack>
        )}
      </Box>
    </HStack>
  );
};

const MobileMenu: React.FC<{ pathnameWithoutLocale: string }> = (props) => {
  const locale = useLocaleValue();
  const t = useI18n();
  const { onClose, onOpen, open } = useDisclosure();
  const searchParams = useSearchParams();

  return (
    <>
      <IconButton aria-label="menu" size="sm" variant="outline" onClick={onOpen}>
        <Icon icon="menu" />
      </IconButton>

      <DrawerRoot
        open={open}
        placement="start"
        onOpenChange={({ open }) => {
          if (!open) onClose();
        }}
      >
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerCloseTrigger />
          <DrawerHeader asChild borderBottomWidth="1px">
            <NextLinkWithoutPrefetch href={`/${locale}`}>
              <Image alt={t.core.title()} h={5} src="/language-neutral/images/exercode.svg" />
            </NextLinkWithoutPrefetch>
          </DrawerHeader>
          <DrawerBody>
            <Stack mx={-4}>
              {getMenuItems(locale).map(([href, label]) => (
                <Button key={href} asChild justifyContent="flex-start" variant="ghost">
                  <NextLinkWithoutPrefetch href={href}>{label}</NextLinkWithoutPrefetch>
                </Button>
              ))}
              <Separator />
              {LOCALES_INCLUDING_NON_STANDARD.map((l) => (
                <Button key={l} asChild justifyContent="flex-start" variant="ghost">
                  <NextLinkWithoutPrefetch
                    href={{ pathname: `/${l}${props.pathnameWithoutLocale}`, query: searchParams.toString() }}
                  >
                    {t.components.header.locales[l]()}
                  </NextLinkWithoutPrefetch>
                </Button>
              ))}
              <Separator />
              <Text color="fg.muted" fontSize="sm" px={4}>
                {t.components.header.relatedSites()}
              </Text>
              {getRelatedSiteItems(locale).map(([href, label]) => (
                <Button key={href} asChild justifyContent="flex-start" variant="ghost">
                  <NextLinkWithoutPrefetch href={href} rel="noopener noreferrer" target="_blank">
                    <HStack>
                      <Text>{label}</Text>
                      <Icon icon="openInNew" />
                    </HStack>
                  </NextLinkWithoutPrefetch>
                </Button>
              ))}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};

function getMenuItems(locale: string, contestId?: string, isVisible?: boolean): [string, string][] {
  const t = i18n(locale);
  const menuItems: [string, string][] = contestId
    ? isVisible
      ? [
          [`/${locale}/contests/${contestId}`, t.components.contestHeader.home()],
          [`/${locale}/contests/${contestId}/standings`, t.components.contestHeader.standings()],
          [`/${locale}/contests/${contestId}/problems`, t.components.contestHeader.problems()],
          [
            `/${locale}/submissions?${new URLSearchParams({ authorId: 'self', contestId })}`,
            t.components.contestHeader.submissions(),
          ],
        ]
      : [[`/${locale}/contests/${contestId}`, t.components.contestHeader.home()]]
    : [
        [`/${locale}/courses`, t.components.header.courses()],
        [`/${locale}/contests`, t.components.header.contests()],
      ];
  menuItems.push([`/${locale}/help`, t.components.header.help()]);
  return menuItems;
}

function getRelatedSiteItems(locale: string): [string, string][] {
  const t = i18n(locale);
  return [[`https://ai-game-builder.willbooster.com/`, t.components.header.llmApp()]];
}

const signOut = async (path: string): Promise<void> => {
  await authClient.signOut({
    fetchOptions: {
      onSuccess: () => {
        location.href = path;
      },
    },
  });
};
